import { ConditionsSheetsSelection } from '@src/common/components/ConditionsSheetsSelection';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { DocumentFormFileUpload } from '@src/documents/components/DocumentFormFileUpload';
import { FormFileModes } from '@src/documents/components/FormFileModes';
import type { FormDocumentType } from '@src/documents/components/UpsertFormDocument';
import type { PermitApplication } from '@src/orders/domain/PermitApplication';
import type { CompletePermitSchemaType } from '@src/permits/completePermit/fields/CompletePermitFields';
import type { StatePermitFormType } from '@src/permits/completePermit/StatePermitDropzone';
import { StatePermitDropzone } from '@src/permits/completePermit/StatePermitDropzone';
import { StatePermitInfo } from '@src/permits/completePermit/StatePermitInfo';
import type { Control, UseFormGetValues } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import type { UseFormSetValue } from 'react-hook-form/dist/types/form';
import { useTranslation } from 'react-i18next';

interface Props {
  permitApplication: PermitApplication;
  orderId: string;
  control: Control<CompletePermitSchemaType>;
  getValues: UseFormGetValues<CompletePermitSchemaType>;
  readOnly?: boolean;
  isSelfIssued?: boolean;
  validateExpirationDate: boolean;
  validateExpirationTime: boolean;
  setValue: UseFormSetValue<CompletePermitSchemaType>;
}

export const PermitDetails = ({
  permitApplication,
  isSelfIssued,
  validateExpirationDate,
  validateExpirationTime,
  control,
  getValues,
  readOnly,
  orderId,
  setValue,
}: Props) => {
  const { t } = useTranslation();
  const { fields: sheetsField } = useFieldArray({
    control,
    name: 'conditionsSheets',
    keyName: 'array-id',
  });

  return (
    <Stack gap={theme.spacing['6']} paddingX={theme.spacing['3']}>
      <Typography variant="h6" className={categoryTitle}>
        {t('permitInfo')}
      </Typography>

      <StatePermitInfo
        control={control}
        readOnly={readOnly}
        validateExpirationDate={validateExpirationDate}
        validateExpirationTime={validateExpirationTime}
      />

      <Typography variant="h6" className={categoryTitle}>
        {t('statePermitDocument')}
      </Typography>

      <StatePermitDropzone
        isSelfIssued={isSelfIssued}
        getValues={getValues}
        orderId={orderId}
        permitApplication={permitApplication}
        readOnly={readOnly}
        control={
          control as unknown as Control<
            CompletePermitSchemaType & StatePermitFormType
          >
        }
        setValue={setValue as unknown as UseFormSetValue<StatePermitFormType>}
      />

      <Typography variant="h6" className={categoryTitle}>
        {t('permitDocuments')}
      </Typography>

      <DocumentFormFileUpload
        isSmall
        orderId={orderId}
        permitApplicationId={permitApplication.id}
        control={control as unknown as Control<FormDocumentType>}
        readOnly={readOnly}
        mode={FormFileModes.SENT_TO_CLIENT}
      />

      <ConditionsSheetsSelection
        control={control}
        conditionsSheets={sheetsField}
        readOnly={readOnly}
      />
    </Stack>
  );
};

const categoryTitle = css(() => ({
  borderBottom: '1px solid',
}));
